import moment from 'moment'
import Vue from 'vue'
import { API, SportsKind, DataFreshnessKind } from '@/models/core'
import { Enum } from '@/helpers/enum'
import { KabaddiPlayer, KabaddiPlayerRole, KabaddiPlayerRoleOrder } from './player'

export const KabaddiMatchStatusKind = new Enum({
  Started: 'started',
  NotStarted: 'not_started',
  Completed: 'completed'
})

export class KabaddiTimelineItem {
  static createFromJson (raw, match) {
    let item = new KabaddiTimelineItem()
    item = Object.assign(item, raw)
    let comment = ''

    if (item.raiding) {
      item.team = item.raiding.team
      comment = `${match.players[item.raiding.player].name} ${item.raiding.points} pts`

      if (item.defending.player) {
        comment += ` < ${match.players[item.defending.player].name} ${item.defending.points} pts`
      }
    } else if (item.player) {
      comment = `${match.players[item.player].name}`
    } else if (item.kind === 'substitution') {
      comment = `${match.players[item.player_in].name} In & ${match.players[item.player_out].name} Out`
    }

    item.comment = comment

    return item
  }
}

export class KabaddiMatch {
  constructor () {
    this.sport = SportsKind.Kabaddi
  }

  get dataFreshness () {
    let freshness = DataFreshnessKind.Warm
    if (this.status.isStarted) {
      freshness = DataFreshnessKind.Live
    } else if (this.status.isCompleted) {
      const delta = Math.abs(this.start_at.diff(moment(), 'minutes'))
      if (delta < (20 + 15)) {
        freshness = DataFreshnessKind.Hot
      }
    } else if (this.status.isNotStarted) {
      const delta = Math.abs(this.start_at.diff(moment(), 'minutes'))
      if (delta < 15) {
        freshness = DataFreshnessKind.Hot
      }
    }
    return freshness
  }

  groupPlayersByRole (playerKeys, players) {
    let playerRoles = []
    let roleMap = {}
    KabaddiPlayerRoleOrder.forEach((role, index) => {
      playerRoles.push({ name: role.name, players: [] })
      if (role.keys === null) {
        roleMap['others'] = index
      } else {
        role.keys.forEach((key) => { roleMap[key] = index })
      }
    })

    playerKeys.forEach((playerKey) => {
      const player = KabaddiPlayer.createFromJson(playerKey, players[playerKey])
      let role = player.parsedRole
      let index = roleMap['others']

      if (roleMap.hasOwnProperty(role.key)) {
        index = roleMap[role.key]
      }
      playerRoles[index].players.push(player.key)
    })

    return playerRoles
  }

  teamPlayersByRole (teamKey) {
    const playerList = this.teams[teamKey].match.players
    return this.groupPlayersByRole(playerList, this.players)
  }

  get requiresLiveUpdate () {
    return this.dataFreshness.isLive || this.dataFreshness.isHot
  }

  get minuteLabel () {
    if (this.sessions) {
      let minute = 0
      let otherLabel = null
      this.sessions.forEach((session) => {
        otherLabel = null
        if (session.end != null) {
          if (session.key === 'first_half' || session.key === 'second_half') {
            minute += 45
            otherLabel = (session.key === 'first_half') ? 'HT' : null
          } else {
            minute += parseInt(session.length_sec / 60)
          }
        } else {
          const start = moment.unix(session.start)
          const sessionMinute = start.diff(new Date(), 'minutes') * -1
          minute += sessionMinute
        }
      })

      if (otherLabel) {
        return otherLabel
      }
      return `${minute}'`
    } else {
      return ''
    }
  }

  get havePlayersInfo () {
    if (!this.teams) {
      return false
    }

    const teamA = this.teams.a
    if (teamA.match && teamA.match.players && teamA.match.players.length > 0) {
      return true
    }

    const teamB = this.teams.b
    if (teamB.match && teamB.match.players && teamB.match.players.length > 0) {
      return true
    }

    return false
  }

  updateLatest (useFresh) {
    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }

    return API.instance().get(`/kabaddi/match/${this.key}/`, params).then((resp) => {
      const updatedMatch = KabaddiMatch.createFromJson(resp.data.data.match)
      this.overwrite(updatedMatch)
    })
  }

  overwrite (updatedMatch) {
    Object.entries(updatedMatch).forEach(obj => Vue.set(this, obj[0], obj[1]))
  }

  static createFromJson (raw) {
    let match = new KabaddiMatch()
    match = Object.assign(match, raw)
    match.start_at = moment.unix(match.start_date.timestamp)
    match.slotTime = match.start_at
    match.status = KabaddiMatchStatusKind.parse(match.status)
    // if (match.players) {
    //   Object.keys(match.players).forEach((key) => {
    //     match.players[key].role = KabaddiPlayerRole.parse(match.players[key].role)
    //   })
    // }
    if (match.play_by_play) {
      match.sessions = []
      let lastSession = null
      let items = []
      match.play_by_play.forEach((item) => {
        if (lastSession == null) {
          lastSession = item.state
        }

        if (item.state !== lastSession) {
          match.sessions.unshift({ key: lastSession, items })
          items = []
          lastSession = item.state
        }

        items.unshift(KabaddiTimelineItem.createFromJson(item, match))
      })

      if (items.length > 0) {
        match.sessions.unshift({ key: lastSession, items })
      }
    }

    return match
  }

  static fetch (key, useFresh) {
    if (!key) {
      console.log('Key is empty', key, useFresh)
      return null
    }

    let params = {}
    if (useFresh) {
      params['headers'] = API.getUseFreshHeader()
    }

    return API.instance().get(`/kabaddi/match/${key}/`, params).then((resp) => {
      return KabaddiMatch.createFromJson(resp.data.data.match)
    })
  }
}
